<script>
  import { onMount } from "svelte";
  import { ListItem, Icon, Badge, Popup, Page, Navbar, NavRight, Link, Button, Block } from "framework7-svelte";
  import { BASE_URL } from "../services/service.config";

  export let title = "";
  export let url = "";
  export let docType = "general"; // 'pan' | 'cheque' | 'signature' | 'profile' | 'general'
  export let pennyDropStatus = false;
  export let pennyDropDetails = null; // optional penny_drop_response
  export let accountDetails = {}; // { account_number, ifsc, bank_name, account_type }

  let isPdf = false;
  let isImage = false;
  let isPennyDropVerified = false;
  let imgLoadError = false;
  let canvasEl;
  let popupOpened = false;
  let cleanedUrl = "";
  let proxyViewUrl = "";
  let proxyDownloadUrl = "";
  let isRenderingPdf = false;
  let canvasRendered = false;

  $: {
    cleanedUrl = cleanDocUrl(url);
    const lower = cleanedUrl.toLowerCase();
    isPdf = Boolean(
      cleanedUrl && (
        lower.endsWith(".pdf") ||
        lower.includes(".pdf?") ||
        lower.includes(".pdf#") ||
        lower.includes("/pdf") ||
        lower.includes("format=pdf") ||
        lower.includes("type=pdf") ||
        lower.includes("application/pdf")
      )
    );
    isImage = Boolean(cleanedUrl && !isPdf);
    isPennyDropVerified = docType === "cheque" && Boolean(
      pennyDropStatus === true ||
      pennyDropStatus === "true" ||
      pennyDropStatus === "success" ||
      pennyDropStatus === "COMPLETED" ||
      pennyDropStatus === "verified" ||
      pennyDropStatus === "VERIFIED"
    );

    if (cleanedUrl) {
      proxyViewUrl = getDocumentProxyUrl(cleanedUrl, false, title);
      proxyDownloadUrl = getDocumentProxyUrl(cleanedUrl, true, title);
    } else {
      proxyViewUrl = "";
      proxyDownloadUrl = "";
    }
  }

  function cleanDocUrl(raw) {
    if (!raw || typeof raw !== "string" || raw.trim() === "" || raw === "undefined" || raw === "null") {
      return "";
    }
    let str = raw.trim().replace(/^["']|["']$/g, "");
    if (str.startsWith("http%3A") || str.startsWith("https%3A")) {
      try {
        str = decodeURIComponent(str);
      } catch (e) {}
    }
    return str;
  }

  function getDocumentProxyUrl(rawUrl, isDownload = false, name = "") {
    if (!rawUrl) return "";
    if (rawUrl.includes("/api/v1/documents/proxy")) return rawUrl;

    const base = BASE_URL || "https://api.trdr.money";
    const safeName = (name || "document").replace(/[^a-zA-Z0-9_-]/g, "_");
    const ext = isPdf ? ".pdf" : "";
    const filename = `${safeName}${ext}`;

    let endpoint = `${base}/api/v1/documents/proxy?url=${encodeURIComponent(rawUrl)}`;
    if (isDownload) {
      endpoint += "&download=true";
    }
    if (filename) {
      endpoint += `&filename=${encodeURIComponent(filename)}`;
    }
    return endpoint;
  }

  // PDF.js dynamic thumbnail renderer (best effort, graceful fallback to icon)
  async function loadPdfJs() {
    if (window.pdfjsLib) return window.pdfjsLib;
    return new Promise((resolve, reject) => {
      if (document.getElementById("pdfjs-cdn-script")) {
        let check = setInterval(() => {
          if (window.pdfjsLib) {
            clearInterval(check);
            resolve(window.pdfjsLib);
          }
        }, 50);
        return;
      }
      const script = document.createElement("script");
      script.id = "pdfjs-cdn-script";
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js";
      script.onload = () => {
        try {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
        } catch (e) {}
        resolve(window.pdfjsLib);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  $: if (isPdf && canvasEl && proxyViewUrl && !canvasRendered) {
    renderPdfThumbnail(proxyViewUrl);
  }

  async function renderPdfThumbnail(pdfUrl) {
    if (!pdfUrl) return;
    try {
      isRenderingPdf = true;
      const pdfjs = await loadPdfJs();
      const loadingTask = pdfjs.getDocument({
        url: pdfUrl,
        verbosity: 0,
      });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.25 });
      if (!canvasEl) return;
      canvasEl.height = 44;
      canvasEl.width = 44;
      const renderContext = {
        canvasContext: canvasEl.getContext("2d"),
        viewport: viewport,
      };
      await page.render(renderContext).promise;
      canvasRendered = true;
    } catch (e) {
      canvasRendered = false;
    } finally {
      isRenderingPdf = false;
    }
  }

  function handleClick() {
    if (!cleanedUrl) {
      return;
    }
    popupOpened = true;
  }
</script>

<ListItem
  title={title}
  style={cleanedUrl ? "cursor: pointer;" : ""}
  on:click={handleClick}
>
  <div slot="media" class="doc-media-wrapper">
    {#if isPdf}
      <div class="pdf-thumbnail-container" title="PDF Document - Click to preview">
        <canvas bind:this={canvasEl} class="pdf-canvas {canvasRendered ? 'visible' : 'hidden'}" width="44" height="44"></canvas>
        {#if !canvasRendered}
          <div class="pdf-badge-icon">
            <span class="pdf-badge-label">PDF</span>
            <Icon f7="doc_text_fill" size="22" color="red" />
          </div>
        {/if}
      </div>
    {:else if isImage && !imgLoadError}
      <!-- svelte-ignore a11y-missing-attribute -->
      <img
        alt={title}
        src={proxyViewUrl || cleanedUrl}
        class="doc-img-thumbnail"
        on:error={() => { imgLoadError = true; }}
      />
    {:else if isImage && imgLoadError}
      <div class="doc-fallback-box error" title="Image failed to load">
        <Icon f7="exclamationmark_triangle_fill" size="20" color="orange" />
      </div>
    {:else if isPennyDropVerified}
      <div class="penny-drop-chip" title="Bank account verified via Penny Drop (No cheque upload required)">
        <Icon f7="checkmark_shield_fill" size="24" color="green" />
      </div>
    {:else}
      <div class="doc-fallback-box empty" title="No document uploaded">
        <Icon f7="photo_fill_on_rectangle_fill" size="20" color="gray" />
      </div>
    {/if}
  </div>

  <div slot="after" class="doc-after-wrapper">
    {#if isPennyDropVerified}
      <span class="badge color-green" style="font-size: 11px; font-weight: 600; margin-bottom: 2px;">
        ✅ Penny Drop Verified
      </span>
      {#if accountDetails && accountDetails.account_number}
        <div class="penny-drop-subtext">
          A/C: {accountDetails.account_number} | {accountDetails.ifsc || ''}
        </div>
      {/if}
    {/if}

    {#if isPdf}
      <div class="doc-actions-pill">
        <a
          href={proxyViewUrl || cleanedUrl}
          target="_blank"
          rel="noopener noreferrer"
          class="external pdf-open-link-btn"
          external
          title="Open PDF directly in a new tab"
          on:click|stopPropagation
        >
          📄 Open PDF
          <Icon f7="arrow_up_right_square" size="12" style="margin-left: 3px;" />
        </a>
      </div>
    {:else if isImage && !imgLoadError}
      <span class="badge color-blue" style="font-size: 11px; cursor: pointer;">
        🔍 View Image
      </span>
    {:else if isImage && imgLoadError}
      <span class="badge color-orange" style="font-size: 11px;">
        ⚠️ S3 Error
      </span>
    {:else if !isPennyDropVerified}
      <span class="badge color-gray" style="font-size: 11px;">
        Not Uploaded
      </span>
    {/if}
  </div>
</ListItem>

<!-- Preview Popup Modal -->
<Popup class="doc-preview-popup" opened={popupOpened} onPopupClosed={() => { popupOpened = false; }}>
  <Page>
    <Navbar title={title}>
      <NavRight>
        {#if proxyViewUrl || cleanedUrl}
          <a
            href={proxyViewUrl || cleanedUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="external link icon-only"
            external
            title="Open original file in new tab"
          >
            <Icon f7="arrow_up_right_square" size="18" />
          </a>
          <a
            href={proxyDownloadUrl || cleanedUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            class="external link icon-only"
            external
            title="Download file"
          >
            <Icon f7="arrow_down_circle" size="18" />
          </a>
        {/if}
        <Link popupClose iconF7="xmark_circle_fill" />
      </NavRight>
    </Navbar>
    <Block class="doc-modal-content">
      <div class="modal-toolbar">
        <div class="modal-doc-meta">
          <Icon f7={isPdf ? "doc_text_fill" : "photo_fill"} size="16" color={isPdf ? "red" : "blue"} style="margin-right: 6px;" />
          <span class="modal-doc-name">{title}</span>
        </div>
        {#if proxyViewUrl || cleanedUrl}
          <div class="modal-btn-group">
            <a
              href={proxyViewUrl || cleanedUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="external button button-fill color-blue modal-cta-btn"
              external
            >
              <Icon f7="arrow_up_right_square" size="14" style="margin-right: 5px;" />
              Open in New Tab
            </a>
            <a
              href={proxyDownloadUrl || cleanedUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              class="external button button-outline color-blue modal-cta-btn"
              external
            >
              <Icon f7="arrow_down_circle" size="14" style="margin-right: 5px;" />
              Download
            </a>
          </div>
        {/if}
      </div>

      {#if isPdf}
        <div class="pdf-viewer-frame">
          <object
            data={proxyViewUrl || cleanedUrl}
            type="application/pdf"
            class="pdf-object-viewer"
            title={title}
          >
            <div class="pdf-fallback-card">
              <Icon f7="doc_text_fill" size="48" color="red" />
              <p>Preview cannot be embedded directly in this browser.</p>
              <div style="display: flex; gap: 8px; justify-content: center;">
                <a
                  href={proxyViewUrl || cleanedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="external button button-fill color-blue"
                  external
                >
                  Open PDF in New Tab
                </a>
                <a
                  href={proxyDownloadUrl || cleanedUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  class="external button button-outline color-blue"
                  external
                >
                  Download PDF
                </a>
              </div>
            </div>
          </object>
        </div>
      {:else if isImage}
        <div class="img-viewer-container">
          <!-- svelte-ignore a11y-missing-attribute -->
          <img
            src={proxyViewUrl || cleanedUrl}
            alt={title}
            class="modal-full-img"
          />
        </div>
      {/if}
    </Block>
  </Page>
</Popup>

<style>
  .doc-media-wrapper {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    overflow: hidden;
    background: #f1f3f5;
    border: 1px solid #dee2e6;
  }
  .doc-img-thumbnail {
    width: 44px;
    height: 44px;
    object-fit: cover;
    display: block;
  }
  .pdf-thumbnail-container {
    width: 44px;
    height: 44px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff5f5;
  }
  .pdf-canvas {
    width: 44px;
    height: 44px;
    object-fit: cover;
  }
  .pdf-canvas.visible {
    display: block;
  }
  .pdf-canvas.hidden {
    display: none;
  }
  .pdf-badge-icon {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .pdf-badge-label {
    font-size: 8px;
    font-weight: 800;
    color: #e03131;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }
  .penny-drop-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: #ebfbee;
  }
  .doc-fallback-box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
  }
  .doc-after-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
  }
  .doc-actions-pill {
    display: flex;
    align-items: center;
  }
  .pdf-open-link-btn {
    text-decoration: none;
    background: #fee2e2;
    color: #dc2626;
    border: 1px solid #fecaca;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .pdf-open-link-btn:hover {
    background: #fca5a5;
    color: #991b1b;
  }
  .penny-drop-subtext {
    font-size: 10px;
    color: #495057;
    margin-top: 1px;
  }
  .doc-modal-content {
    height: calc(100vh - 70px);
    display: flex;
    flex-direction: column;
    padding: 12px;
    box-sizing: border-box;
    overflow-y: auto;
  }
  .modal-toolbar {
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .modal-doc-meta {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
  }
  .modal-doc-name {
    letter-spacing: 0.2px;
  }
  .modal-btn-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .modal-cta-btn {
    width: auto !important;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    height: 30px;
    line-height: 30px;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 6px;
  }
  .pdf-viewer-frame {
    flex: 1;
    width: 100%;
    min-height: 520px;
    height: calc(100vh - 150px);
    display: flex;
    flex-direction: column;
    background: #475569;
    border-radius: 8px;
    overflow: hidden;
  }
  .pdf-object-viewer {
    width: 100%;
    height: 100%;
    flex: 1;
    border: none;
  }
  .pdf-fallback-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 24px;
    color: #ffffff;
    text-align: center;
    gap: 16px;
  }
  .img-viewer-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
    background: #212529;
    border-radius: 6px;
    padding: 16px;
  }
  .modal-full-img {
    max-width: 100%;
    max-height: 75vh;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  }
</style>
